/**
 * Утилита для автоматического обновления токена доступа
 * Обновляет токен за 5 минут до истечения
 */

import { apiClient } from "../config/api";

let refreshTimer = null;
let tokenExpirationTime = null;

/**
 * Устанавливает таймер для автоматического обновления токена
 * @param {number} expiresIn - Время жизни токена в секундах
 */
export function setupTokenRefresh(expiresIn) {
  // Очищаем предыдущий таймер если есть
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  if (!expiresIn || expiresIn <= 0) {
    console.warn("⚠️ Invalid token expiration time:", expiresIn);
    return;
  }

  // Сохраняем время истечения токена
  tokenExpirationTime = Date.now() + expiresIn * 1000;

  // Обновляем токен за 5 минут до истечения (или за половину времени, если токен живет меньше 10 минут)
  const refreshBeforeExpiry = Math.min(5 * 60 * 1000, (expiresIn * 1000) / 2);
  const refreshTime = expiresIn * 1000 - refreshBeforeExpiry;

  console.log(
    `⏰ Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`
  );

  refreshTimer = setTimeout(async () => {
    try {
      console.log("🔄 Auto-refreshing access token...");
      const tokens = await apiClient.refreshAccessToken();

      if (tokens && tokens.access_token) {
        console.log("✅ Token auto-refreshed successfully");
        
        // Сбрасываем счётчик попыток
        window.tokenRefreshRetries = 0;
        
        // Используем expires_in из ответа сервера
        const expiresIn = tokens.expires_in || 3600; // По умолчанию 1 час если не указано
        
        // Обновляем tokenExpiry
        const expiryTime = Date.now() + expiresIn * 1000;
        localStorage.setItem("tokenExpiry", expiryTime.toString());
        tokenExpirationTime = expiryTime;
        
        console.log(`⏱️  New token expires in ${expiresIn}s (${Math.round(expiresIn/60)} minutes)`);
        
        // Устанавливаем новый таймер
        setupTokenRefresh(expiresIn);
      }
    } catch (error) {
      console.error("❌ Auto token refresh failed:", error);
      
      // Если ошибка "No refresh token available" - перенаправляем на логин
      if (error.message === "No refresh token available") {
        console.log("🔒 Refresh token missing - redirecting to login");
        apiClient.handleUnauthorized();
        return;
      }
      
      // Если ошибка 400 или 401 - refresh token невалиден, перенаправляем на логин
      if (error.status === 400 || error.status === 401) {
        console.log("🔒 Refresh token invalid (HTTP " + error.status + ") - redirecting to login");
        window.tokenRefreshRetries = 0;
        apiClient.handleUnauthorized();
        return;
      }
      
      // Для других ошибок пробуем снова через минуту (максимум 3 попытки)
      const retryCount = (window.tokenRefreshRetries || 0) + 1;
      window.tokenRefreshRetries = retryCount;
      
      if (retryCount <= 3) {
        console.log(`⏰ Retry token refresh in 1 minute... (attempt ${retryCount}/3)`);
        setTimeout(() => {
          setupTokenRefresh(60); // Попробуем через минуту
        }, 60 * 1000);
      } else {
        console.log("🔒 Max retry attempts reached - redirecting to login");
        window.tokenRefreshRetries = 0;
        apiClient.handleUnauthorized();
      }
    }
  }, refreshTime);
}

/**
 * Останавливает автоматическое обновление токена
 */
export function stopTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
    tokenExpirationTime = null;
    console.log("⏹️ Token auto-refresh stopped");
  }
}

/**
 * Возвращает оставшееся время до истечения токена в секундах
 */
export function getTimeUntilExpiry() {
  if (!tokenExpirationTime) {
    return null;
  }

  const remaining = Math.max(0, tokenExpirationTime - Date.now());
  return Math.floor(remaining / 1000);
}

/**
 * Проверяет наличие и валидность токенов
 * Возвращает true если токены валидны, иначе false
 */
export function validateTokens() {
  const accessToken = apiClient.getAccessToken();
  const refreshToken = apiClient.getRefreshToken();
  
  // Если нет ни access, ни refresh токена - невалидно
  if (!accessToken && !refreshToken) {
    console.warn("⚠️ No tokens found");
    return false;
  }
  
  // Если нет refresh токена, но есть access - невалидно (не сможем обновить)
  if (!refreshToken) {
    console.warn("⚠️ No refresh token available");
    return false;
  }
  
  // Токены валидны
  return true;
}

/**
 * Проверяет, нужно ли обновить токен сейчас
 * (если до истечения осталось меньше 5 минут)
 */
export function shouldRefreshNow() {
  const remaining = getTimeUntilExpiry();
  if (remaining === null) {
    return false;
  }

  // Обновляем если осталось меньше 5 минут
  return remaining < 5 * 60;
}
